import { ReactNode, useRef } from 'react';
import {
  Box,
  FelaCSS,
  FelaCSSWithCustomSelectors,
  TSetRef,
  TSizeValue,
  TSpacingValue,
} from '@bridebook/ui';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import CarouselHeading from './carousel-heading';
import styles from './carousel-simple.style';
import SimpleSliderDesktop, {
  CarouselNavigationType,
} from './simple-slider-desktop/simple-slider-desktop';
import SimpleSliderMobile from './simple-slider-mobile/simple-slider-mobile';

export interface ICarouselSimpleProps {
  slides: Array<any>;
  show?: number;
  tabletShow?: number;
  infinite?: boolean;
  noReinit?: boolean;
  onSlide?: (index: number) => void;
  fixedWidthSlide?: number;
  fixedWidthSlideMobile?: TSizeValue;
  carouselTitle?: ReactNode;
  navigationType?: CarouselNavigationType;
  headerButtons?: ReactNode;
  isMobile: boolean;
  isTablet: boolean;
  setMobileSliderRef?: TSetRef | ReturnType<typeof useRef>;
  headingStyles?: FelaCSS;
  controlsStyles?: FelaCSS;
  containerStyles?: FelaCSS;
  headingStylesWrap?: FelaCSS;
  noSSR?: boolean;
  slideStyles?: FelaCSSWithCustomSelectors;
  onTouchEnd?: () => void;
  noHeadingPadding?: boolean;
  noPaddingLeft?: boolean; //temporary solution, to be removed after merging the explore tab with home page
  gap?: TSpacingValue;
}

const CarouselSimple = ({
  slides,
  show = 6,
  tabletShow,
  isMobile,
  isTablet,
  onSlide,
  noReinit,
  fixedWidthSlide,
  fixedWidthSlideMobile,
  carouselTitle,
  navigationType = 'top',
  headerButtons = null,
  setMobileSliderRef,
  headingStyles = {},
  controlsStyles = {},
  containerStyles,
  noSSR = true,
  slideStyles,
  headingStylesWrap = {},
  onTouchEnd,
  noPaddingLeft,
  gap,
}: ICarouselSimpleProps) => {
  const dataSize = slides ? slides.length : 0;
  const showNav = !isMobile && dataSize > show;
  const isDesktop = !isTablet && !isMobile;
  const slidesToShow = (() => {
    if (isMobile && dataSize > 1) return 2;
    if (isTablet) return tabletShow || 3;

    return isDesktop ? show : 1;
  })();

  return dataSize > 0 ? (
    <Box data-name="carousel" style={styles.sliderWrap}>
      {isMobile || isTablet ? (
        <>
          {carouselTitle && (
            <CarouselHeading
              style={mergeStyles([styles.headingWrap(noPaddingLeft), headingStylesWrap])}>
              <Box style={headingStyles}>{carouselTitle}</Box>
              {headerButtons}
            </CarouselHeading>
          )}
          <SimpleSliderMobile
            slides={slides}
            setRef={setMobileSliderRef}
            slideWidth={fixedWidthSlideMobile}
            containerStyles={containerStyles}
            slideStyles={slideStyles}
            onTouchEnd={onTouchEnd}
            onSlide={onSlide}
            noPadding={noPaddingLeft}
            gap={gap}
          />
        </>
      ) : (
        <SimpleSliderDesktop
          headingStyles={headingStyles}
          slideStyles={slideStyles}
          slides={slides}
          slidesToShow={slidesToShow}
          showNav={showNav}
          onSlide={onSlide}
          noReinit={noReinit}
          fixedWidthSlide={fixedWidthSlide}
          headerTitle={carouselTitle}
          navigationType={navigationType}
          headerButtons={headerButtons}
          controlsStyles={controlsStyles}
          containerStyles={containerStyles}
          noSSR={noSSR}
          gap={gap}
        />
      )}
    </Box>
  ) : null;
};

export default CarouselSimple;
